import React, { useState } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Container,
} from '@mui/material';
import { Plus, Minus } from 'lucide-react';

// Original FAQ Data Preserved
const faqData = [
    {
        question: 'What is Bizstor and how can it help my business?',
        answer:
            'Bizstor is a modern accounting system that helps you manage invoices, inventory, returns, and payments—all from one dashboard.',
    },
    {
        question: 'Can I create and send invoices with Bizstor?',
        answer:
            'Yes, Bizstor allows you to easily create and send invoices to your customers directly from the platform.',
    },
    {
        question: 'How does Bizstor manage inventory?',
        answer:
            'Bizstor provides real-time inventory tracking so you always know what’s in stock, what’s low, and when to restock.',
    },
    {
        question: 'Does Bizstor support product return management?',
        answer:
            'Yes, you can process product returns efficiently and track return histories to maintain accurate inventory and financial records.',
    },
    {
        question: 'Is Bizstor suitable for small businesses?',
        answer:
            'Absolutely. Bizstor is designed to be user-friendly, affordable, and scalable for all type of businesses.',
    },
];

const FaqSection = () => {
    // Original State Logic Preserved
    const [expanded, setExpanded] = useState(false);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    return (
        <Box 
            sx={{ 
                width: '100%', 
                py: { xs: 8, md: 12 }, 
                backgroundColor: '#F8FAFC' // Consistent Fintech Light background 
            }}
        >
            <Container maxWidth="md">
                
                {/* Modern Header Section */}
                <Typography
                    variant="h2"
                    align="center"
                    sx={{
                        color: '#0F172A',
                        fontWeight: 800,
                        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                        letterSpacing: '-0.02em',
                        mb: 2,
                    }}
                >
                    Frequently Asked <Box component="span" sx={{ color: '#4F46E5' }}>Questions</Box>
                </Typography>
                
                <Typography
                    variant="body1"
                    align="center"
                    sx={{
                        color: '#475569',
                        fontSize: '1.125rem',
                        mb: 6,
                        maxWidth: '600px',
                        mx: 'auto'
                    }}
                >
                    Everything you need to know about the product and billing. Can't find the answer you're looking for? Please chat to our friendly team.
                </Typography>

                {/* FAQ Accordion List */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {faqData.map((item, index) => {
                        const isExpanded = expanded === index;

                        return (
                            <Accordion
                                key={index}
                                expanded={isExpanded}
                                onChange={handleChange(index)}
                                disableGutters // Removes default MUI margin jumping
                                elevation={0}  // Removes default shadow
                                sx={{
                                    borderRadius: '16px !important', // Forces radius on all states
                                    border: isExpanded ? '2px solid #4F46E5' : '1px solid #E2E8F0',
                                    boxShadow: isExpanded ? '0 10px 25px -5px rgba(79, 70, 229, 0.1)' : 'none',
                                    backgroundColor: '#ffffff',
                                    transition: 'all 0.2s ease-in-out',
                                    '&:before': { display: 'none' }, // Hides default MUI divider line
                                    '&:hover': {
                                        borderColor: isExpanded ? '#4F46E5' : '#CBD5E1',
                                    },
                                }}
                            >
                                <AccordionSummary
                                    expandIcon={
                                        // Modern dynamic icon styling
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 32,
                                                height: 32,
                                                borderRadius: '50%',
                                                backgroundColor: isExpanded ? '#4F46E5' : '#F1F5F9',
                                                color: isExpanded ? '#ffffff' : '#64748B',
                                                transition: 'all 0.3s ease',
                                            }}
                                        >
                                            {isExpanded ? <Minus size={18} /> : <Plus size={18} />}
                                        </Box>
                                    }
                                    aria-controls={`panel${index}-content`}
                                    id={`panel${index}-header`}
                                    sx={{
                                        px: { xs: 2, sm: 3 },
                                        py: 1,
                                    }}
                                >
                                    <Typography 
                                        sx={{ 
                                            fontWeight: 600, 
                                            fontSize: '1.05rem',
                                            color: isExpanded ? '#0F172A' : '#334155',
                                            transition: 'color 0.2s ease'
                                        }}
                                    >
                                        {item.question}
                                    </Typography>
                                </AccordionSummary>
                                
                                <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 3, pt: 0 }}>
                                    <Typography 
                                        sx={{ 
                                            color: '#475569', 
                                            lineHeight: 1.6,
                                            fontSize: '0.95rem',
                                            maxWidth: '90%'
                                        }}
                                    >
                                        {item.answer}
                                    </Typography>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>
            </Container>
        </Box>
    );
};

export default FaqSection;